import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Button,
  DialogActions,
  TextField,
  FormControlLabel,
  Checkbox,
  Box
} from '@mui/material';
import { Demandes } from '../../models/Demandes';
import { API_CONFIG } from '../../api/config';
import useIsMobile from '../../hooks/useIsMobile';

interface DemandeActionsLegrandProps {
  open: boolean;
  onClose: () => void;
  selectedIds: number[];
  email: string;
  onRefresh: () => void;
}

interface DemandeAPI extends Demandes {
  libelle_visa?: string;
}

const DemandeActionsLegrand: React.FC<DemandeActionsLegrandProps> = ({
  open,
  onClose,
  selectedIds,
  email,
  onRefresh
}) => { 
  const [objetMail, setObjetMail] = useState('');
  const [alerte, setAlerte] = useState(false);
  const [envoyerMail, setEnvoyerMail] = useState(true);
  const [messageAppli, setMessageAppli] = useState('');
  const [messageMail, setMessageMail] = useState('');
  const [currentActionType, setCurrentActionType] = useState<string>('');
  const [localEmail, setLocalEmail] = useState(email || '');
  const [selectedDemandes, setSelectedDemandes] = useState<DemandeAPI[]>([]);
  const isMobile = useIsMobile();

  // Permet de remplacer les sauts de ligne par <br> pour l'envoi HTML
  const formattedMessageMail = messageMail.replace(/\n/g, '<br>');

  // Récupérer les demandes sélectionnées
  useEffect(() => {
    const fetchSelectedDemandes = async () => {
      if (selectedIds.length > 0) {
        try {
          const token = localStorage.getItem('token_legrand');
          const promises = selectedIds.map(id =>
            fetch(`${API_CONFIG.baseUrl}/api/legrand/demandes/${id}`, {
              headers: {
                'Authorization': `Bearer ${token}`
              }
            }).then(res => res.json())
          );
          const demandes = await Promise.all(promises);
          setSelectedDemandes(demandes);
        } catch (error) {
          console.error('Erreur lors de la récupération des demandes:', error);
        }
      }
    };

    if (open) {
      fetchSelectedDemandes();
    }
  }, [open, selectedIds]);

  // Remise à zéro / initialisation lorsque la modal s'ouvre ou se ferme
  useEffect(() => {
    if (!open) {
      setObjetMail('');
      setAlerte(false);
      setEnvoyerMail(true);
      setMessageAppli('');
      setMessageMail('');
    } else {
      // Une seule demande sélectionnée
      if (selectedDemandes.length === 1) {
        const d = selectedDemandes[0];
        setObjetMail(`Dossier ${d.id}`);

        // Récupérer infosdemande si présent
        if (d.infosdemande) {
          setMessageAppli(d.infosdemande);
        }
      }
    }
  }, [open, selectedDemandes, selectedIds]);

  useEffect(() => {
    if (open) {
      setLocalEmail(email || '');
    }
  }, [open, email]);

  const handleValidation = async () => {
    const dataToSend = {
      ids: selectedIds,
      email: localEmail,
      objet: objetMail,
      alerte,
      envoyerMail,
      messageAppli,
      messageMail: formattedMessageMail,
      actionType: currentActionType
    };

    try {
      const token = localStorage.getItem('token_legrand');
      await fetch(`${API_CONFIG.baseUrl}/api/legrand/actionsDemande`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(dataToSend),
      });

      onClose();
      onRefresh();
    } catch (error) {
      console.error('Erreur lors de l\'envoi des actions:', error);
    }
  };

  const isEvisa = (demande: DemandeAPI): boolean => {
    // Vérifier d'abord libelle_visa (depuis l'API), sinon titre
    const libelle = demande.libelle_visa || demande.titre || '';
    return libelle.toLowerCase().includes('evisa');
  };

  const buildObject = (demande: Demandes, suffix: string) => {
    return `${demande.nom} ${demande.prenom} - Dossier ${demande.id} - ${suffix}`;
  };

  /**
   * Mail problème (Visa ou eVisa)
   */
  const fillProblemMessage = () => {
    if (selectedDemandes.length === 0) return;

    const demande = selectedDemandes[0];
    const finalSubject = buildObject(
      demande,
      'Action requise : Informations ou pièces justificatives erronées dans votre dossier'
    );

    const body = `
Bonjour ${demande.prenom},

Après examen de votre dossier, nous constatons certaines informations ou pièces justificatives non conformes.
Afin de compléter votre dossier et de poursuivre le traitement, merci de bien vouloir apporter les modifications nécessaires.

Détails des éléments à corriger :
(Nom du document/information) : (Description de l'erreur ou du problème rencontré)
(Nom du document/information) : (Description de l'erreur ou du problème rencontré)
(Nom du document/information) : (Description de l'erreur ou du problème rencontré)

Actions à entreprendre : Merci de bien vouloir nous faire parvenir en réponse à ce mail les éléments cités ci-dessus afin que nos équipes corrigent votre dossier et traitent votre demande.

Pour toute question, contactez-nous sur contact@visamonde.com

Cordialement,
L'équipe Visa Monde

<img src="https://ci3.googleusercontent.com/mail-sig/AIorK4z4GRH3V7qan5qlurDL6pCLND7O3V30v9b0KuMKCQ7QVYfS1CdbtJy00i9apP08aQbZOanAi9E" alt="Visa Monde" style="max-width: 200px; margin-top: 10px;">

https://visamonde.com
89 boulevard national
92250 LA GARENNE COLOMBES
(+33)1 86 22 08 06
contact@visamonde.com

---`;

    setObjetMail(finalSubject);
    setMessageMail(body);
    setCurrentActionType('visa_probleme');
  };

  /**
   * Mail "visa en cours"
   */
  const fillInProgressMessage = () => {
    if (selectedDemandes.length === 0) return;

    const demande = selectedDemandes[0];
    const finalSubject = `${demande.nom} ${demande.prenom} - Dossier ${demande.id} - Demande est complet`;

    const body = `Bonjour ${demande.prenom},

Votre dossier a été vérifié par nos services, il est conforme aux attentes des services consulaires concernés.
Nos agents procéderont à la demande dans les plus brefs délais.
Vous serez tenu informé de l'évolution de votre demande à chaque étape.

Pour toute question, contactez-nous sur contact@visamonde.com

Cordialement,
L'équipe Visa Monde

<img src="https://ci3.googleusercontent.com/mail-sig/AIorK4z4GRH3V7qan5qlurDL6pCLND7O3V30v9b0KuMKCQ7QVYfS1CdbtJy00i9apP08aQbZOanAi9E" alt="Visa Monde" style="max-width: 200px; margin-top: 10px;">

https://visamonde.com
89 boulevard national
92250 LA GARENNE COLOMBES
(+33)1 86 22 08 06
contact@visamonde.com`;

    setObjetMail(finalSubject);
    setMessageMail(body);
    setCurrentActionType('visa_en_cours');
  };

  /**
   * Mail "visa plus tard"
   */
  const fillVisaLaterMessage = () => {
    if (selectedDemandes.length === 0) return;

    const demande = selectedDemandes[0];
    const finalSubject = buildObject(demande, 'Votre dossier est complet');

    const body = `
Bonjour ${demande.prenom},

Votre dossier a été vérifié par nos services, il est conforme aux attentes des services consulaires concernés.

Nous constatons que votre date de départ ne permet pas d'effectuer la demande dès maintenant sur le site officiel de demande de visa.

En effet, si la demande était créée maintenant, le visa ne serait plus valable pour votre date de départ.

Pas d'inquiétude, votre dossier est conservé précieusement par nos agents et sera traité dès que la validité du visa permettra de le faire.

Vous serez tenu informé de l'évolution de votre demande à chaque étape.

Pour toute question, contactez-nous sur contact@visamonde.com

Cordialement,
L'équipe Visa Monde

<img src="https://ci3.googleusercontent.com/mail-sig/AIorK4z4GRH3V7qan5qlurDL6pCLND7O3V30v9b0KuMKCQ7QVYfS1CdbtJy00i9apP08aQbZOanAi9E" alt="Visa Monde" style="max-width: 200px; margin-top: 10px;">

https://visamonde.com
89 boulevard national
92250 LA GARENNE COLOMBES
(+33)1 86 22 08 06
contact@visamonde.com

---`;

    setObjetMail(finalSubject);
    setMessageMail(body);
    setCurrentActionType('visa_plus_tard');
  };

  /**
   * Mail "Adresse de réexpédition"
   */
  const fillAdresseRetourMessage = () => {
    if (selectedDemandes.length === 0) return;

    const demande = selectedDemandes[0];
    const finalSubject = buildObject(demande, 'Adresse de réexpédition');

    const body = `
Bonjour ${demande.prenom},

Afin de préparer le retour de votre passeport, merci de bien vouloir nous faire parvenir par retour de mail l'adresse à laquelle vous souhaitez le recevoir.

Si vous avez choisi un retour par DHL, merci d'indiquer :
- Le nom et prénom du destinataire
- Si retour adresse professionnelle, nom de la société
- Adresse complète
- Le numéro de téléphone du destinataire
- L'adresse mail à laquelle vous souhaitez recevoir le suivi DHL

Si vous avez choisi un retour en livraison (Paris et proche banlieue uniquement)
- Le nom et prénom du destinataire
- Si retour adresse professionnelle, nom de la société
- Adresse complète (code porte, étage, bâtiment…)
- Le numéro de téléphone du destinataire

Vous recevrez l'information de la réexpédition dès réception du visa par nos services.

Pour toute question, contactez-nous sur contact@visamonde.com

Cordialement,
L'équipe Visa Monde

<img src="https://ci3.googleusercontent.com/mail-sig/AIorK4z4GRH3V7qan5qlurDL6pCLND7O3V30v9b0KuMKCQ7QVYfS1CdbtJy00i9apP08aQbZOanAi9E" alt="Visa Monde" style="max-width: 200px; margin-top: 10px;">

https://visamonde.com
89 boulevard national
92250 LA GARENNE COLOMBES
(+33)1 86 22 08 06
contact@visamonde.com

---`;

    setObjetMail(finalSubject);
    setMessageMail(body);
    setCurrentActionType('adresse_retour');
  };

  /**
   * Mail "Dossier reçu" (pour visas uniquement, pas e-visas)
   * Change etat_visa = 'Dossier reçu' et etat_paiement = 'à facturer'
   */
  const fillDossierRecuMessage = () => {
    if (selectedDemandes.length === 0) return;

    const demande = selectedDemandes[0];
    const finalSubject = buildObject(demande, 'Réception de votre dossier');

    const body = `
Bonjour ${demande.prenom},

Nous vous confirmons la bonne réception de votre dossier de demande de visa.

Nos équipes vont procéder à la vérification de l'ensemble des documents et vous tiendront informé de l'avancement de votre demande à chaque étape.

Si des documents sont manquants ou non conformes, nous reviendrons vers vous dans les plus brefs délais.

Pour toute question, contactez-nous sur contact@visamonde.com

Cordialement,
L'équipe Visa Monde

<img src="https://ci3.googleusercontent.com/mail-sig/AIorK4z4GRH3V7qan5qlurDL6pCLND7O3V30v9b0KuMKCQ7QVYfS1CdbtJy00i9apP08aQbZOanAi9E" alt="Visa Monde" style="max-width: 200px; margin-top: 10px;">

https://visamonde.com
89 boulevard national
92250 LA GARENNE COLOMBES
(+33)1 86 22 08 06
contact@visamonde.com

---`;

    setObjetMail(finalSubject);
    setMessageMail(body);
    setCurrentActionType('dossier_recu');
  };

  /**
   * Mail avec signature standard (visa et evisa)
   * Juste pour envoyer un mail, sans modifier l'état du dossier
   */
  const fillSignatureMessage = () => {
    if (selectedDemandes.length === 0) return;

    const demande = selectedDemandes[0];
    const finalSubject = buildObject(demande, 'Information concernant votre dossier');

    const body = `
Bonjour ${demande.prenom},

[Votre message personnalisé ici]

Pour toute question, contactez-nous sur contact@visamonde.com

Cordialement,
L'équipe Visa Monde

<img src="https://ci3.googleusercontent.com/mail-sig/AIorK4z4GRH3V7qan5qlurDL6pCLND7O3V30v9b0KuMKCQ7QVYfS1CdbtJy00i9apP08aQbZOanAi9E" alt="Visa Monde" style="max-width: 200px; margin-top: 10px;">

https://visamonde.com
89 boulevard national
92250 LA GARENNE COLOMBES
(+33)1 86 22 08 06
contact@visamonde.com

---`;

    setObjetMail(finalSubject);
    setMessageMail(body);
    setCurrentActionType('mail_signature');
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth fullScreen={isMobile}>
      <DialogTitle>Actions pour la demande sélectionnée</DialogTitle>
      <DialogContent>
        <TextField
          margin="dense"
          label="Email du client"
          type="email"
          fullWidth
          variant="outlined"
          value={localEmail || ''}
          onChange={(e) => setLocalEmail(e.target.value)}
        />
        <TextField
          margin="dense"
          label="Objet mail"
          type="text"
          fullWidth
          variant="outlined"
          value={objetMail}
          onChange={(e) => setObjetMail(e.target.value)}
        />
        <FormControlLabel
          control={<Checkbox checked={alerte} onChange={(e) => setAlerte(e.target.checked)} />}
          label="Alerte"
        />
        <FormControlLabel
          control={<Checkbox checked={envoyerMail} onChange={(e) => setEnvoyerMail(e.target.checked)} />}
          label="Envoyer mail"
        />
        <TextField
          margin="dense"
          label="Message Appli"
          type="text"
          fullWidth
          variant="outlined"
          value={messageAppli}
          onChange={(e) => setMessageAppli(e.target.value)}
        />
        <TextField
          margin="dense"
          label="Message Mail"
          multiline
          rows={15}
          fullWidth
          variant="outlined"
          value={messageMail}
          onChange={(e) => setMessageMail(e.target.value)}
        />

        {/* Boutons pour pré-remplir les mails */}
        <Box display="flex" justifyContent="center" gap={2} mt={2} flexWrap="wrap">
          <Button onClick={fillProblemMessage} variant="contained" color="primary">
            Mail Visa(s) Problème
          </Button>
          <Button onClick={fillInProgressMessage} variant="contained" color="primary">
            Mail Visa(s) en Cours
          </Button>
        </Box>

        <Box display="flex" justifyContent="center" gap={2} mt={2} flexWrap="wrap">
          <Button onClick={fillVisaLaterMessage} variant="contained" color="primary">
            Mail Visa Plus Tard
          </Button>
          {/* Afficher le bouton Mail Adresse Retour uniquement si ce n'est pas un eVisa */}
          {selectedDemandes.length > 0 &&
           !isEvisa(selectedDemandes[0]) && (
            <Button onClick={fillAdresseRetourMessage} variant="contained" color="primary">
              Mail Adresse Retour
            </Button>
          )}
        </Box>

        <Box display="flex" justifyContent="center" gap={2} mt={2} flexWrap="wrap">
          {/* Afficher le bouton Dossier Reçu uniquement pour les visas classiques (pas e-visas) */}
          {selectedDemandes.length > 0 &&
           !isEvisa(selectedDemandes[0]) && (
            <Button onClick={fillDossierRecuMessage} variant="contained" color="success">
              Mail Dossier Reçu
            </Button>
          )}
          {/* Bouton Mail Signature (disponible pour tous: visa ET evisa) */}
          <Button onClick={fillSignatureMessage} variant="contained" color="info">
            Mail Signature
          </Button>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Annuler</Button>
        <Button onClick={handleValidation} variant="contained">Valider</Button>
      </DialogActions>
    </Dialog>
  );
};

export default DemandeActionsLegrand;